import { useState } from "react";
import { Link } from "react-router-dom";
import Footer from "../Footer/Footer";
import { Helmet } from "react-helmet-async";
import { useI18n } from "../../../i18n/I18nContext";

const faqs = [
  {
    question: "Que tipo de racks e estantes fornecem?",
    answer:
      "Disponibilizamos soluções para cargas leves, médias e pesadas, incluindo porta-paletes, cantilever, picking e soluções personalizadas.",
  },
  {
    question: "Fazem instalação?",
    answer:
      "Sim. Disponibilizamos serviço profissional de instalação e montagem.",
  },
  {
    question: "Prestam manutenção?",
    answer: "Não. A Racksforall ainda não conta com este serviço.",
  },
  {
    question: "Os sistemas cumprem com as normas de segurança?",
    answer:
      "Sim. Trabalhamos com materiais certificados segundo as normas europeias.",
  },
  {
    question: "Podem ajudar a projetar um armazém novo?",
    answer: "Sim. Prestamos consultoria para novos projetos.",
  },
  {
    question: "Trabalham apenas em Portugal?",
    answer:
      "Não. Temos a capacidade de apoiar projetos, tanto em Portugal, como em outros mercados europeus.",
  },
  {
    question: "As soluções são apenas para armazéns?",
    answer:
      "Não. As nossas soluções são versáteis, seja para PME's (pequenas e médias empresas), retalho e até mesmo para a sua casa, como por exemplo, a sua garagem.",
  },
];

function FaqItem({ question, answer, index }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className={`group border-b border-black/10 transition-colors ${open ? "bg-indigo-50/60" : "hover:bg-gray-50"
        }`}
    >
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-start justify-between gap-6 px-6 py-5 text-left"
        aria-expanded={open}
      >
        <div className="flex items-start gap-4">
          <span
            className={`mt-0.5 shrink-0 text-xs font-bold tabular-nums transition-colors ${open ? "text-indigo-600" : "text-gray-300 group-hover:text-gray-400"
              }`}
          >
            {String(index + 1).padStart(2, "0")}
          </span>
          <span
            className={`text-base font-semibold leading-snug transition-colors ${open ? "text-indigo-700" : "text-gray-900"
              }`}
          >
            {question}
          </span>
        </div>

        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-all ${open
              ? "border-indigo-200 bg-indigo-600 text-white"
              : "border-gray-200 bg-white text-gray-400 group-hover:border-gray-300"
            }`}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
            }}
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
      </button>

      <div
        style={{
          display: "grid",
          gridTemplateRows: open ? "1fr" : "0fr",
          transition: "grid-template-rows 0.25s ease",
        }}
      >
        <div className="overflow-hidden">
          <p className="pb-5 pl-14 pr-10 text-sm leading-7 text-gray-600">
            {answer}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function Faq() {
  const { t } = useI18n();

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Helmet>
        <title>{t("Perguntas Frequentes | Racksforall")}</title>
        <meta name="description" content={t("Respostas às perguntas mais comuns sobre os nossos produtos, montagem, envios e garantias.")} />
        <link rel="canonical" href="https://racksforall.pt/faq" />
        <meta property="og:title" content={t("FAQ | Racksforall")} />
        <meta property="og:url" content="https://racksforall.pt/faq" />
      </Helmet>
      <main className="flex-1 pt-20">

        {/* Hero compacto */}
        <section className="border-b border-black/10 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">
            <div className="flex items-start gap-6">
              <div className="hidden sm:flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-indigo-600 text-white shadow-md">
                <svg
                  className="w-7 h-7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z"
                  />
                </svg>
              </div>

              <div>
                <span className="text-xs font-semibold uppercase tracking-[0.22em] text-indigo-600">
                  FAQ
                </span>
                <h1 className="mt-1 text-3xl sm:text-4xl font-black tracking-tight text-gray-950">
                  {t("Perguntas frequentes")}
                </h1>
                <p className="mt-3 text-gray-500 max-w-xl leading-7">
                  {t("Respostas às questões mais comuns sobre os nossos produtos, serviços e processo de trabalho.")}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ list */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">
          <div className="grid lg:grid-cols-12 gap-10">

            {/* Sidebar fixa */}
            <aside className="hidden lg:block lg:col-span-4">
              <div className="lg:sticky lg:top-28 space-y-6">
                <div className="rounded-2xl border border-black/10 bg-gray-50 p-6">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400 mb-3">
                    {t("Nesta página")}
                  </p>
                  <nav className="space-y-1">
                    {faqs.map((faq, i) => (
                      <p
                        key={i}
                        className="text-sm text-gray-600 leading-snug py-1.5 border-b border-black/5 last:border-b-0"
                      >
                        <span className="text-gray-300 font-bold tabular-nums mr-2">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        {t(faq.question)}
                      </p>
                    ))}
                  </nav>
                </div>


              </div>
            </aside>

            {/* Accordion */}
            <div className="lg:col-span-8">
              <div className="rounded-2xl border border-black/10 bg-white overflow-hidden shadow-sm divide-y-0">
                {faqs.map((faq, i) => (
                  <FaqItem
                    key={i}
                    index={i}
                    question={t(faq.question)}
                    answer={t(faq.answer)}
                  />
                ))}
              </div>

              {/* CTA mobile */}
              <div className="mt-8 lg:hidden rounded-2xl bg-indigo-700 p-6 text-white">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-200 mb-2">
                  {t("Ainda tem questões?")}
                </p>
                <p className="text-sm leading-7 text-indigo-100 mb-4">
                  {t("Fale diretamente com a nossa equipa.")}
                </p>
                <a
                  href="mailto:clients@racksforall.eu"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2.5 text-sm font-semibold text-indigo-700 hover:bg-indigo-50 transition-colors"
                >
                  clients@racksforall.eu
                </a>
              </div>

              {/* CTA desktop */}
              <div className="mt-8 hidden lg:flex items-center justify-between rounded-2xl border border-black/10 bg-gray-50 px-7 py-5">
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {t("Ainda tem questões?")}
                  </p>
                  <a
                    href="mailto:clients@racksforall.eu"
                    className="text-sm text-indigo-600 hover:text-indigo-800 transition-colors font-medium"
                  >
                    clients@racksforall.eu
                  </a>
                </div>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center rounded-full bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-gray-700 transition-colors"
                >
                  {t("Contacte-nos")}
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}